import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Work from "./components/Work";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import AdminPage from "./components/AdminPage";
import CaseStudy from "./components/CaseStudy";
import { CASE_STUDIES } from "./data/projects";
import { scrollToId } from "./lib/scroll";
import { API_BASE } from "./lib/api";

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: "#070709", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "sans-serif", textAlign: "center", padding: 24 }}>
          <p style={{ margin: 0, fontSize: 12, letterSpacing: "0.3em", color: "#f45d2c" }}>PIX® MARKETING</p>
          <p style={{ margin: 0, fontSize: 16 }}>Something went wrong.</p>
          <a href="/" style={{ color: "#f45d2c", fontSize: 14 }}>Back to home</a>
        </div>
      );
    }
    return this.props.children;
  }
}

function Site() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const active = id ? CASE_STUDIES.find((p) => p.id === id) : null;

  useEffect(() => {
    const target = !active && location.state?.scrollTo;
    if (!target) return;
    const timers = [0, 300, 700, 1200].map((d) =>
      setTimeout(() => scrollToId(target), d)
    );
    return () => timers.forEach(clearTimeout);
  }, [active, location.state]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const nextProject = () => {
    if (!active) return;
    const i = CASE_STUDIES.findIndex((p) => p.id === active.id);
    navigate(`/work/${CASE_STUDIES[(i + 1) % CASE_STUDIES.length].id}`);
  };

  if (id && !active) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-ink font-body text-white">
      <Nav />
      {active ? (
        <CaseStudy
          key={active.id}
          project={active}
          onClose={() => navigate("/")}
          onNext={nextProject}
        />
      ) : (
        <main>
          <Hero />
          <Work />
          <Contact />
        </main>
      )}
      <Footer />
    </div>
  );
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return undefined;
    const visitId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `v-${Date.now()}-${Math.random()}`;
    const t0 = Date.now();
    const send = (ev, dur) => {
      const body = JSON.stringify({
        event: ev,
        path: location.pathname,
        referrer: document.referrer || "",
        screen_w: window.innerWidth,
        visit_id: visitId,
        duration_s: dur,
      });
      fetch(`${API_BASE}/api/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body,
        keepalive: true,
      }).catch(() => {});
    };
    send("enter");
    const onLeave = () => send("leave", Math.round((Date.now() - t0) / 1000));
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      onLeave();
    };
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <PageTracker />
          <Routes>
            <Route path="/" element={<Site />} />
            <Route path="/work/:id" element={<Site />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </MotionConfig>
    </ErrorBoundary>
  );
}
